import { DotsLoader } from 'Components/DotsLoader';
import { ButtonForm } from 'Components/Form/styles';
import { ChipsInput } from 'Components/common/ChipsInput';
import { Column, Row } from 'Components/Grid';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { useCountryConfig } from 'hooks/useCountryConfig';
import { validateTaxId, stripTaxIdFormatting } from 'utils/tax_id_validators';
import { useMutation, useQuery } from 'react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { SubtitleLogin, TitleLogin } from './styles';
import { createCompany, getCompanyShortBySlug } from 'services/company-service';
import { useTranslation } from 'react-i18next';
import { Dialog, Select, TextField } from 'Components/ui';

export const OnboardCompany = () => {
  const { t } = useTranslation(['company']);
  const { idOnboard } = useParams();
  const navigate = useNavigate();
  const { data: company } = useQuery(['getCompanyBySlug'], async () => {
    const response = await getCompanyShortBySlug('vo');
    return response.data;
  });

  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
    getValues,
  } = useForm({
    defaultValues: {
      name: '',
      corporateName: '',
      slug: '',
      cnpj: '',
      size: '',
      income: '',
      industry: '',
      description: '',
      active: true,
      partner: false,
      managers: [],
      roles: [],
      domains: [],
      signedPlan: '',
      country: '',
      defaultLanguage: '',
    },
  });

  const countryValue = useWatch({ control, name: 'country' });
  const { taxIdLabel, taxIdMask, taxIdPlaceholder, countryConfig, allConfigs, isError: isCountryConfigError, isLoading: isCountryConfigLoading } = useCountryConfig(countryValue);

  const mutation = useMutation(
    newCompany => {
      return createCompany(newCompany, idOnboard);
    },
    {
      onError: error => {
        // An error happened!
        Dialog.alert({
          title: t('company:onboard.company.errorTitle'),
          message: error.response.data.message ?? error.message,
          icon: 'error',
        });
      },
      onSuccess: data => {
        if (data.status === 201) {
          const companyData = getValues();
          navigate(`/onboard-company-admin/${companyData.slug}`);
        }
      },
    },
  );

  const onSubmit = () => {
    if (!company) return;
    const companyData = getValues();
    mutation.mutate({
      name: companyData.name,
      corporateName: companyData.corporateName,
      cnpj: stripTaxIdFormatting(companyData.cnpj),
      slug: companyData.slug,
      size: companyData.size,
      income: companyData.income,
      industry: companyData.industry,
      domains: companyData.domains,
      country: companyData.country,
      defaultLanguage: companyData.defaultLanguage,
      managers: [
        {
          _id: company._id,
          name: company.name,
        },
      ],
      partner: false,
      active: true,
      signedPlan: 'Free',
      roles: [],
    });
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Row justifyContent="center" className="mt-5">
        <Column xs={12} className="text-center mb-3">
          <TitleLogin>Virtual</TitleLogin>
          <TitleLogin className="bold"> Officer</TitleLogin>
        </Column>
        <Column xs={12} className="text-center mb-4">
          <SubtitleLogin>{t('company:onboard.company.subtitle')}</SubtitleLogin>
        </Column>
      </Row>
      {!mutation.isSuccess && (
        <>
          <Row justifyContent="center">
            <Column xs={8} sm={8} className="mx-auto mb-4" justifyContent="center" flex>
              <TextField
                {...register('name', { required: t('company:onboard.company.nameRequired') })}
                label={t('company:onboard.company.name')}
                name="name"
                error={errors?.name ? true : false}
                helperText={errors?.name ? errors.name.message : null}
                fullWidth
              />
            </Column>
          </Row>
          <Row justifyContent="center">
            <Column xs={8} sm={8} className="mx-auto mb-4" justifyContent="center" flex>
              <TextField
                {...register('corporateName', { required: t('company:onboard.company.corporateNameRequired') })}
                label={t('company:onboard.company.corporateName')}
                name="corporateName"
                error={errors?.corporateName ? true : false}
                helperText={errors?.corporateName ? errors.corporateName.message : null}
                fullWidth
              />
            </Column>
          </Row>
          <Row justifyContent="center">
            <Column xs={8} sm={4} className="ml-auto mb-4 pr-2" justifyContent="center" flex>
              <Controller
                control={control}
                name="country"
                rules={{ required: t('company:onboard.company.countryRequired') }}
                render={({ field: { onChange, value } }) => (
                  <Select
                    name="country"
                    value={value}
                    onChange={onChange}
                    label={t('company:onboard.company.country')}
                    error={!!errors?.country || isCountryConfigError}
                    helperText={isCountryConfigError ? t('company:onboard.company.countryErrorHelper') : errors?.country?.message}
                    disabled={isCountryConfigError}
                    options={[
                      {
                        value: '',
                        label: isCountryConfigError ? t('company:onboard.company.countryError') : t('company:onboard.company.countryPlaceholder'),
                        disabled: true,
                      },
                      ...allConfigs.map(cc => ({ value: cc.countryCode, label: cc.name.pt })),
                    ]}
                  />
                )}
              />
            </Column>
            <Column xs={8} sm={4} className="mr-auto mb-4 pl-2" justifyContent="center" flex>
              <Controller
                control={control}
                name="defaultLanguage"
                rules={{ required: t('company:onboard.company.languageRequired') }}
                render={({ field: { onChange, value } }) => (
                  <Select
                    name="defaultLanguage"
                    value={value}
                    onChange={onChange}
                    label={t('company:onboard.company.language')}
                    error={!!errors?.defaultLanguage}
                    helperText={errors?.defaultLanguage?.message}
                    options={[
                      { value: '', label: t('company:onboard.company.languagePlaceholder'), disabled: true },
                      { value: 'pt', label: t('company:onboard.company.languagePt') },
                      { value: 'en', label: t('company:onboard.company.languageEn') },
                      { value: 'es', label: t('company:onboard.company.languageEs') },
                    ]}
                  />
                )}
              />
            </Column>
          </Row>
          <Row justifyContent="center">
            <Column xs={8} sm={4} className="ml-auto mb-4 pr-2" justifyContent="center" flex>
              <TextField
                {...register('slug', { required: t('company:onboard.company.slugRequired') })}
                label={t('company:onboard.company.slug')}
                name="slug"
                error={errors?.slug ? true : false}
                helperText={errors?.slug ? errors.slug.message : null}
                fullWidth
              />
            </Column>
            <Column xs={8} sm={4} className="mr-auto mb-4 pl-2" justifyContent="center" flex>
              <TextField
                {...register('cnpj', {
                  required: `${taxIdLabel || 'Tax ID'} é obrigatório`,
                  validate: (value) => {
                    if (!value || !countryValue) return true;
                    if (isCountryConfigLoading) return 'Aguardando configuração de país...';
                    if (!countryConfig) return true;
                    const stripped = stripTaxIdFormatting(value);
                    // Only run format regex when value has visual separators (formatted input)
                    // Skip for already-stripped DB values on edit to avoid false failures
                    if (stripped !== value && countryConfig.taxId?.regex) {
                      const formatOk = new RegExp(countryConfig.taxId.regex).test(value);
                      if (!formatOk) return `Formato inválido. Use: ${taxIdMask}`;
                    }
                    const checkOk = validateTaxId(stripped, countryValue);
                    if (!checkOk) return `${taxIdLabel} inválido. Use o formato: ${taxIdMask}`;
                    return true;
                  },
                })}
                label={taxIdLabel || 'CNPJ'}
                name="cnpj"
                placeholder={taxIdPlaceholder}
                error={errors?.cnpj ? true : false}
                helperText={errors?.cnpj ? errors.cnpj.message : null}
                fullWidth
              />
            </Column>
          </Row>
          <Row justifyContent="center">
            <Column xs={8} sm={4} className="ml-auto mb-4 pr-2" justifyContent="center" flex>
              <TextField
                {...register('size', { required: t('company:onboard.company.sizeRequired') })}
                label={t('company:onboard.company.size')}
                name="size"
                error={errors?.size ? true : false}
                helperText={errors?.size ? errors.size.message : null}
                fullWidth
              />
            </Column>
            <Column xs={8} sm={4} className="mr-auto mb-4 pl-2" justifyContent="center" flex>
              <TextField
                {...register('income', { required: t('company:onboard.company.incomeRequired') })}
                label={t('company:onboard.company.income')}
                name="income"
                error={errors?.income ? true : false}
                helperText={errors?.income ? errors.income.message : null}
                fullWidth
              />
            </Column>
          </Row>
          <Row justifyContent="center">
            <Column xs={8} sm={8} className="mx-auto mb-4" justifyContent="center" flex>
              <TextField
                {...register('industry', { required: t('company:onboard.company.industryRequired') })}
                label={t('company:onboard.company.industry')}
                name="industry"
                error={errors?.industry ? true : false}
                helperText={errors?.industry ? errors.industry.message : null}
                fullWidth
              />
            </Column>
          </Row>
          <Row justifyContent="center">
            <Column xs={8} sm={8} className="mx-auto mb-4">
              <Controller
                control={control}
                name="domains"
                render={({ field: { onChange, value } }) => (
                  <ChipsInput
                    value={value}
                    onChange={onChange}
                    placeholder={t('company:onboard.company.domainsPlaceholder')}
                  />
                )}
              />
            </Column>
          </Row>
          <Row>
            <Column xs={8} sm={8} className="mx-auto mb-4">
              <TextField
                {...register('description')}
                label={t('company:onboard.company.description')}
                multiline
                rows={4}
                fullWidth
                sx={{ margin: '0 auto 24px' }}
              />
            </Column>
          </Row>
          <Row justifyContent="center" className="mb-3">
            <Column xs={8} sm={6} className="mx-auto mb-2" justifyContent="center" flex>
              <ButtonForm
                className="full center"
                type="submit"
              >
                {mutation.isLoading ? <DotsLoader /> : t('company:onboard.company.continue')}
              </ButtonForm>
            </Column>
          </Row>
        </>
      )}
    </form>
  );
};
